import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { Alert, Button, Spinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { User } from '../types';

type State = { status: 'verifying' } | { status: 'done'; user: User } | { status: 'failed' } | { status: 'limited' };

export function VerifyPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { user: current, setUser } = useSession();
  const [state, setState] = useState<State>(token ? { status: 'verifying' } : { status: 'failed' });
  const [resent, setResent] = useState<'idle' | 'sent' | 'limited' | 'error'>('idle');
  const called = useRef(false);

  useEffect(() => {
    if (!token || called.current) return;
    called.current = true; // strict mode mounts twice; the token is one-shot
    api
      .verifyEmail(token)
      .then((user) => {
        setState({ status: 'done', user });
        if (current && current.id === user.id) setUser(user);
      })
      .catch((err: unknown) => setState({ status: err instanceof ApiError && err.is('rate_limited') ? 'limited' : 'failed' }));
  }, [token, current, setUser]);

  const resend = async () => {
    try {
      await api.resendVerification();
      setResent('sent');
    } catch (err) {
      setResent(err instanceof ApiError && err.is('rate_limited') ? 'limited' : 'error');
    }
  };

  if (state.status === 'verifying') {
    return (
      <PublicLayout title={t('verify.title')}>
        <Spinner />
      </PublicLayout>
    );
  }
  if (state.status === 'done') {
    return (
      <PublicLayout title={t('verify.success')} subtitle={t('verify.successHint', { organization: state.user.organization?.name ?? '' })}>
        <Link to="/">
          <Button className="w-full">{t('verify.openAccount')}</Button>
        </Link>
      </PublicLayout>
    );
  }
  if (state.status === 'limited') {
    // the token is not spent: opening the link again later still works
    return (
      <PublicLayout title={t('app.rateLimitedTitle')}>
        <Alert kind="warn">{t('app.rateLimited')}</Alert>
      </PublicLayout>
    );
  }
  return (
    <PublicLayout title={t('verify.failed')} subtitle={t('verify.failedHint')}>
      {current && !current.email_verified ? (
        <>
          {resent === 'sent' && <Alert kind="success">{t('banner.resent')}</Alert>}
          {resent === 'limited' && <Alert kind="error">{t('app.rateLimited')}</Alert>}
          {resent === 'error' && <Alert kind="error">{t('app.error')}</Alert>}
          <Button className="w-full" onClick={() => void resend()} disabled={resent === 'sent'}>
            {t('verify.resend')}
          </Button>
        </>
      ) : (
        <Link to="/login">
          <Button className="w-full">{t('verify.signIn')}</Button>
        </Link>
      )}
    </PublicLayout>
  );
}
